import { Fate } from './';
import {
  FateAttributesStatusEffect,
  FatePermanentStatusEffect,
  FateState,
  FateStateRecord,
  safeTimePeriod,
} from './fate.ruleset';
import { Pet } from '../pet';
import { getStressValue } from '../pet/pet.ruleset';
import { getLocalDate } from 'src/replicant/utils/time';

export type FateSchedule = { state: FateState; timestamp: number };

/**
 * Return permanent status effects summary from combined fate data and state data
 */
export function getPermanentStatusEffects(state: Fate.State, now: number) {
  const result: Record<FatePermanentStatusEffect, FateStateRecord> = {
    evolving: {
      timestamp: getPermanentStatusEffectStatTime(state, 'evolving', now),
      value: hasPermanentStatusEffect(state, 'evolving', now),
    },
    sick: {
      timestamp: getPermanentStatusEffectStatTime(state, 'sick', now),
      value: hasPermanentStatusEffect(state, 'sick', now),
    },
    toothDecay: {
      timestamp: getPermanentStatusEffectStatTime(state, 'toothDecay', now),
      value: hasPermanentStatusEffect(state, 'toothDecay', now),
    },
    scared: {
      timestamp: getPermanentStatusEffectStatTime(state, 'scared', now),
      value: hasPermanentStatusEffect(state, 'scared', now),
    },
    asleep: {
      timestamp: getPermanentStatusEffectStatTime(state, 'asleep', now),
      value: hasPermanentStatusEffect(state, 'asleep', now),
    },
    dying: {
      timestamp: getPermanentStatusEffectStatTime(state, 'dying', now),
      value: hasPermanentStatusEffect(state, 'dying', now),
    },
    dead: {
      timestamp: getPermanentStatusEffectStatTime(state, 'dead', now),
      value: hasPermanentStatusEffect(state, 'dead', now),
    },
    hadVisitor: {
      timestamp: getPermanentStatusEffectStatTime(state, 'hadVisitor', now),
      value: hasPermanentStatusEffect(state, 'hadVisitor', now),
    },
    wantToGoOut: {
      timestamp: getPermanentStatusEffectStatTime(state, 'wantToGoOut', now),
      value: hasPermanentStatusEffect(state, 'wantToGoOut', now),
    },
  };

  return result;
}

/**
 * Return status effects summary from combined fate and state data, similar to getStatusEffects but faster
 */
export function getAllPermanentStatusEffects(state: Fate.State, now: number) {
  const result: Record<FatePermanentStatusEffect, boolean> = {
    evolving: hasPermanentStatusEffect(state, 'evolving', now),
    sick: hasPermanentStatusEffect(state, 'sick', now),
    toothDecay: hasPermanentStatusEffect(state, 'toothDecay', now),
    scared: hasPermanentStatusEffect(state, 'scared', now),
    asleep: hasPermanentStatusEffect(state, 'asleep', now),
    dying: hasPermanentStatusEffect(state, 'dying', now),
    dead: hasPermanentStatusEffect(state, 'dead', now),
    hadVisitor: hasPermanentStatusEffect(state, 'hadVisitor', now),
    wantToGoOut: hasPermanentStatusEffect(state, 'wantToGoOut', now),
  };

  return result;
}

/**
 * Check if pet already has one of status effects
 */
export function hasPermanentStatusEffect(
  state: Fate.State,
  status: FatePermanentStatusEffect,
  now: number,
) {
  const schedule = state.fate.schedule;

  // Ignore evolution for adult pets
  if (status === 'evolving') {
    const evolution = Pet.getters(state).getCurrentEvolution();
    if (evolution && evolution.stage === 'adult') return false;
  }

  // Check sleeping first
  if (status === 'asleep' && Fate.api.shouldBeAsleep(state, now)) {
    return true;
  }

  // If status effect saved
  if (
    state.pet.statusEffects[status].value &&
    now >= state.pet.statusEffects[status].lastUpdatedAt
  ) {
    return true;
  }

  // Check status effect schedule
  if (schedule[status] && now >= schedule[status].timestamp) {
    return true;
  }

  return false;
}

/**
 * Get permanents status effect start time
 */
export function getPermanentStatusEffectStatTime(
  state: Fate.State,
  status: FatePermanentStatusEffect,
  now: number,
) {
  const schedule = state.fate.schedule;

  // Check sleeping first
  if (status === 'asleep' && Fate.api.shouldBeAsleep(state, now)) {
    return now;
  }

  // Check status effect schedule
  if (schedule[status] && now >= schedule[status].timestamp) {
    return schedule[status].timestamp;
  }

  // If status effect saved
  if (state.pet.statusEffects[status]?.lastUpdatedAt) {
    return state.pet.statusEffects[status].lastUpdatedAt;
  }

  return now;
}

/**
 * Get pet attribute based state
 * Attribute status effects based on time and do not use rnd,
 * that are functions of time and cold dynamically changed
 */
export function getAttributeStatusEffect(
  state: Fate.State,
  now: number,
): FateAttributesStatusEffect {
  const attributes = Pet.getters(state).getAllAttributeValues(now);

  switch (true) {
    case attributes.dirtiness > 70:
      return 'very_dirty';
    case attributes.stomach < 15:
      return 'very_hungry';
    case attributes.love < 15:
      return 'very_lonely';
    case attributes.urine > 85:
      return 'very_pee';
    case attributes.dirtiness > 40:
      return 'dirty';
    case attributes.stomach < 40:
      return 'hungry';
    case attributes.love < 40:
      return 'lonely';
    case attributes.urine > 60:
      return 'pee';
    case getStressValue(attributes) < 15:
      return 'happy';
    default:
      return 'normal';
  }
}

/**
 * Get current pet state
 */
export function getCurrentPetState(
  state: Fate.State,
  now: number,
  isVisiting: boolean,
): FateState {
  // Return permanent states first
  // This function will work without this part but potentially it could speedup thing to O(1)
  if (hasPermanentStatusEffect(state, 'dead', now)) return 'dead';
  if (hasPermanentStatusEffect(state, 'asleep', now)) return 'asleep';
  if (hasPermanentStatusEffect(state, 'dying', now)) return 'dying';
  if (hasPermanentStatusEffect(state, 'sick', now)) return 'sick';
  if (!isVisiting) {
    if (hasPermanentStatusEffect(state, 'evolving', now)) return 'evolving';
    if (hasPermanentStatusEffect(state, 'wantToGoOut', now))
      return 'wantToGoOut';
  }
  if (hasPermanentStatusEffect(state, 'toothDecay', now)) return 'toothDecay';
  if (hasPermanentStatusEffect(state, 'scared', now)) return 'scared';

  // Get attribute based states from fate
  const fate = state.fate.schedule;
  const ids = Object.keys(fate) as FateState[];

  const schedule: { state: FateState; timestamp: number }[] = ids
    .map((id) => ({ state: id, timestamp: fate[id].timestamp }))
    .filter((item) => {
      if (!isVisiting) return true;

      // Exclude this states if visiting
      if (
        item.state === 'evolving' ||
        item.state === 'hadVisitor' ||
        item.state === 'wantToGoOut'
      )
        return false;

      return true;
    })
    .sort((a, b) => b.timestamp - a.timestamp); // Reverse order

  const status = schedule.find((item) => now >= item.timestamp)?.state;

  // Return normal by default
  return status ?? 'normal';
}

/**
 * Get current pet state
 */
export function getNextPetState(
  state: Fate.State,
  now: number,
): FateSchedule | null {
  // Get earlier effects
  const currentEffects = getPermanentStatusEffects(state, now);

  // Keep only active status effects
  for (const key in currentEffects) {
    if (!currentEffects[key].value) delete currentEffects[key];
  }

  // Calculate current state
  const effects = {
    ...currentEffects,
    ...state.fate.schedule,
  };
  const ids = Object.keys(effects) as FateState[];

  const schedule: FateSchedule[] = ids
    .map((id) => ({ state: id, timestamp: effects[id].timestamp }))
    .sort((a, b) => b.timestamp - a.timestamp); // Reverse order

  // Current state index
  const index = schedule.findIndex((item) => now >= item.timestamp);

  // No current state
  if (index === -1) return null;

  // Get next state
  const nextState = schedule[index - 1];
  if (nextState) return nextState;

  // return null by default
  return null;
}

/**
 * Prevent certain status changes from occurring in specific time window
 * If fate system schedule the status during the safe time, randomly reschedule it within 2 hours after the safe time ends.
 * e.g., Pets don’t die between 10pm and 9am.
 * If fate system schedules death status at 8am for example, reschedule it randomly sometime between 9am and 11am
 */
export function isSafeTime(now: number, timeZone: string): boolean {
  const jstHours = getLocalDate(now, timeZone).getHours();
  const { startHour, endHour } = safeTimePeriod;
  return jstHours >= startHour || jstHours < endHour;
}

/**
 * Check if fate can use death feature for fate generation for this start timestamp
 */
export function hasDeathFeatureActivated(
  state: Fate.State,
  startAt: number,
): boolean {
  if (!state.fate.deathFeatureActivationAt) return false;

  return startAt >= state.fate.deathFeatureActivationAt;
}
